import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { useFetchEmailSubscriptionList } from '@src/components/notification-channel/EmailSubscriptions/list/hooks';
import useFetchWebhooksList from '@src/components/notification-channel/webhooks/hooks/useWebhooksList';
import { useFetchServiceIntegrationsList } from '@src/components/notification-channel/ServiceIntegrations/list/hooks';
import { Partner } from '@src/components/notification-channel/ServiceIntegrations/types';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { ChannelType, DraftChannel } from '../types';
import { ChannelRow } from '../styled';

interface ChannelAttachProps {
  value?: DraftChannel[];
  onChange?: (channels: DraftChannel[]) => void;
}

const CHANNEL_LABELS: Record<ChannelType, string> = {
  email: 'Email',
  webhook: 'Webhook',
  pagerduty: 'PagerDuty',
  datadog: 'Datadog',
  bigpanda: 'BigPanda',
  googlechat: 'GChat',
};

// Valid channel types per product. VSI (EI) supports every integration; Ad (AI)
// is email + webhook only; DPI (APP) drops BigPanda/Datadog. Mirrors the
// per-product PARTNER_MAPPING used on the Service Integrations screen.
const CHANNELS_BY_MODE: Record<string, ChannelType[]> = {
  EI: ['email', 'webhook', 'pagerduty', 'datadog', 'bigpanda', 'googlechat'],
  AI: ['email', 'webhook'],
  APP: ['email', 'webhook', 'pagerduty', 'googlechat'],
};

// Service-integration channel types resolve to a partner in the SI registry.
const CHANNEL_TO_PARTNER: Partial<Record<ChannelType, Partner>> = {
  pagerduty: 'PagerDuty',
  datadog: 'DataDog',
  bigpanda: 'BigPanda',
  googlechat: 'GoogleChat',
};

// Attach a rule to one or more channel targets ("define once, attach to many").
// The 10-active-rules-per-channel cap is enforced authoritatively by the server;
// we only surface a hint here.
const ChannelAttach = ({ value = [], onChange }: ChannelAttachProps) => {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const { data: subscriptions } = useFetchEmailSubscriptionList(isAdminUser);
  const { context } = window.conviva;
  const { data: webhooks } = useFetchWebhooksList({ context });
  const { data: integrations } = useFetchServiceIntegrationsList();

  const allowedChannelTypes = CHANNELS_BY_MODE[CURRENT_PRODUCT_MODE] || ['email', 'webhook'];
  const channelTypeOptions = allowedChannelTypes.map((type) => ({
    label: CHANNEL_LABELS[type],
    value: type,
  }));

  const emailOptions = [...new Set((subscriptions || []).map((s) => s.email).filter(Boolean))].map(
    (e) => ({ label: e as string, value: e as string }),
  );

  const webhookOptions = ((webhooks as any[]) || []).map((w) => ({
    label: w.name,
    value: w.webhookId,
  }));

  const serviceIntegrationOptions = (channelType: ChannelType) => {
    const partner = CHANNEL_TO_PARTNER[channelType];
    return (integrations || [])
      .filter((i) => i.partnerName === partner && i.configId != null)
      .map((i) => ({ label: i.name || `#${i.configId}`, value: i.configId as number }));
  };

  const rows = value.length ? value : [{ channelType: 'email' as ChannelType, target: '' }];

  const update = (index: number, patch: Partial<DraftChannel>) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange?.(next);
  };

  const addRow = () => onChange?.([...rows, { channelType: 'email', target: '', configId: null }]);

  const removeRow = (index: number) => onChange?.(rows.filter((_, i) => i !== index));

  const renderTarget = (row: DraftChannel, index: number) => {
    if (row.channelType === 'email') {
      const selected = row.targets ?? (row.target ? [row.target] : []);
      return (
        <Select
          style={{ flex: 1 }}
          mode="tags"
          placeholder="john@email.com"
          value={selected}
          options={emailOptions}
          tokenSeparators={[',', ';', ' ']}
          optionFilterProp="label"
          onChange={(targets: string[]) => update(index, { targets, target: null, configId: null })}
        />
      );
    }

    const options =
      row.channelType === 'webhook' ? webhookOptions : serviceIntegrationOptions(row.channelType);
    const selectedConfigs = row.configIds ?? (row.configId != null ? [row.configId] : []);

    return (
      <Select
        style={{ flex: 1 }}
        mode="multiple"
        showSearch
        allowClear
        optionFilterProp="label"
        placeholder={
          row.channelType === 'webhook'
            ? 'Select one or more webhooks'
            : `Select one or more ${CHANNEL_LABELS[row.channelType]} integrations`
        }
        value={selectedConfigs}
        options={options}
        onChange={(configIds: number[]) =>
          update(index, { configIds, configId: null, target: null })
        }
      />
    );
  };

  return (
    <div>
      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Channels</label>
      {rows.map((row, index) => (
        <ChannelRow key={index}>
          <Select
            style={{ width: 140 }}
            value={row.channelType}
            options={channelTypeOptions}
            onChange={(channelType) => update(index, { channelType, target: '', configId: null })}
          />
          {renderTarget(row, index)}
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={rows.length === 1}
            onClick={() => removeRow(index)}
          />
        </ChannelRow>
      ))}
      <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 4 }}>
        Add channel
      </Button>
      <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>
        Up to 10 active rules can be attached to a single channel target.
      </div>
    </div>
  );
};

export default ChannelAttach;
